import BaseModal from "~/components/common/base-modal/BaseModal";
import styles from "./ProcessPaymentModal.module.scss";
import classNames from "classnames/bind";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import { useState, useEffect } from "react";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import { notify } from "~/utils/helper";
import jobDetailsApi from "~/apis/jobDetails";
import { IInvoiceInLogPaymentModal } from "~/utils/interface/jobDetails";

const cx = classNames.bind(styles);

interface ProcessPaymentModalProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  /** Pre-select this invoice (opened from Invoice Detail). */
  invoiceId?: string;
}

const EMPTY_FORM = { invoiceId: "", amount: "" };

const ProcessPaymentModal = (props: ProcessPaymentModalProps) => {
  const { open, onClose, jobId, invoiceId } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  // Only invoices that still have a balance (same list as Log Payment).
  const [invoices, setInvoices] = useState<IInvoiceInLogPaymentModal[]>([]);

  const amountDueOf = (id: string) =>
    Number(invoices.find((invoice) => String(invoice.id) === String(id))?.amountDue) || 0;

  useEffect(() => {
    if (!open || !jobId) return;
    setIsLoading(true);
    setError("");
    jobDetailsApi
      .getListInvoiceInLogPaymentModal(jobId, invoiceId ? { invoiceId } : {})
      .then((res) => {
        const list = res.data?.data || [];
        setInvoices(list);
        const preselected = list.find((invoice) => String(invoice.id) === String(invoiceId));
        setFormData(
          preselected
            ? { invoiceId: String(preselected.id), amount: Number(preselected.amountDue).toFixed(2) }
            : EMPTY_FORM
        );
        if (invoiceId && !preselected) setError("This invoice has no amount due.");
      })
      .catch((err: any) => notify("error", err?.message || "Failed to fetch invoices"))
      .finally(() => setIsLoading(false));
  }, [open, jobId, invoiceId]);

  const handleSelectInvoice = (id: string) => {
    setError("");
    setFormData({ invoiceId: id, amount: amountDueOf(id).toFixed(2) });
  };

  const handleSubmit = async () => {
    const amount = parseFloat(formData.amount);
    const amountDue = amountDueOf(formData.invoiceId);
    if (!formData.invoiceId) return setError("Please select an invoice.");
    if (!(amount > 0) || amount > amountDue + 0.005) {
      return setError(`Amount must be greater than $0.00 and not exceed the amount due ($${amountDue.toFixed(2)}).`);
    }

    // Come back to this invoice's detail after Stripe (success or cancel).
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.set("invoice", formData.invoiceId);

    setIsSubmitting(true);
    try {
      const response = await jobDetailsApi.createStripeCheckout({
        invoiceId: formData.invoiceId,
        amount,
        returnUrl: returnUrl.toString(),
      });
      const checkoutUrl = response.data?.data?.checkout_url;
      if (!checkoutUrl) throw new Error("Failed to get checkout URL");
      window.location.href = checkoutUrl;
    } catch (err: any) {
      notify("error", err?.message || "Failed to process payment");
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(EMPTY_FORM);
    setError("");
    onClose();
  };

  const selectedDue = formData.invoiceId ? amountDueOf(formData.invoiceId) : null;

  return (
    <BaseModal width="90%" maxWidth="600px" open={open} onClose={handleClose}>
      <div className={cx("process-payment-modal-container")}>
        <div className={cx("process-payment-modal-header")}>
          <h3 className={cx("header-title")}>Process Payment Now</h3>
        </div>

        <div className={cx("process-payment-modal-body")}>
          {isLoading ? (
            <div className={cx("loading-container")}>
              <p>Loading invoices...</p>
            </div>
          ) : (
            <div className={cx("form-section")}>
              <div className={cx("row-item")}>
                <label className={cx("row-label")}>Invoice</label>
                <PrimarySelectInput
                  name="invoiceId"
                  placeholder={invoices.length ? "Select invoice" : "No invoice with an amount due"}
                  value={formData.invoiceId}
                  onChange={handleSelectInvoice}
                  options={invoices.map((invoice) => ({
                    label: `${invoice.invoiceUniqueCode || `Invoice #${invoice.id}`} ($${Number(invoice.amountDue).toFixed(2)} due)`,
                    value: String(invoice.id),
                  }))}
                  className={cx("select-input")}
                />
              </div>

              <div className={cx("row-item")}>
                <label className={cx("row-label")}>Amount</label>
                <PrimaryInput
                  name="amount"
                  type="text"
                  placeholder="Enter amount"
                  value={formData.amount}
                  onChange={(e) => {
                    // Only allow numbers and a single decimal point
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    const parts = value.split(".");
                    setError("");
                    setFormData((prev) => ({
                      ...prev,
                      amount: parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : value,
                    }));
                  }}
                  className={cx("input")}
                />
                {selectedDue !== null && (
                  <p className="text-sm text-gray-500 mt-1">Amount due: ${selectedDue.toFixed(2)}</p>
                )}
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}
              <p className="text-sm text-gray-500">
                You will be redirected to Stripe to enter the card details. The payment appears on the invoice once
                Stripe confirms it.
              </p>
            </div>
          )}
        </div>

        <div className={cx("process-payment-modal-footer")}>
          <PrimaryButton
            title="Cancel"
            onClick={handleClose}
            className={cx("cancel-button")}
            disabled={isSubmitting}
          />
          <PrimaryButton
            title="Process Payment"
            onClick={handleSubmit}
            className={cx("submit-button")}
            disabled={isLoading || isSubmitting || !invoices.length}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default ProcessPaymentModal;
